import * as R from 'ramda';
import { schema, normalize } from 'normalizr';
import dayjs from 'dayjs';
import num from 'numeral';
import { v4 } from 'uuid';
import { rewriteConnectUrl } from 'utils/api';
import { FieldData, Modify } from 'Common/types';
import {
  Product,
  LocalVariation,
  ProductVariation,
  Image,
  NormalizedProduct,
  ProductsImportHistoryRecord,
  ProductListRawRecord,
  Category,
  PSGTableDataType,
  NormalizedVariationListRecord,
  StockImportJobEv,
  StocktakingImportJob,
  NormalizedGiftCard,
  GiftcardsImportHistoryRecord,
} from 'utils/apiClient/services/inventory/types';
import { getAttributeSetsFromVariations } from './utils';

const product = new schema.Entity('products');
const service = new schema.Entity('services');
const giftcard = new schema.Entity('giftcards');
const category = new schema.Entity('categories');
const productsImport = new schema.Entity('productImports');
const giftcardsImport = new schema.Entity('giftcardImports');

export const normalizeProducts = (products: ProductListRawRecord[]) =>
  normalize(products, [product]);

export const normalizeServices = (services: ProductListRawRecord[]) =>
  normalize(services, [service]);

export const normalizeGiftcards = (giftcards: ProductListRawRecord[]) =>
  normalize(giftcards, [giftcard]);

export const normalizeVariations = (variations: ProductListRawRecord[]) =>
  normalize(variations, [product]);

export const normalizeCategories = (data: Category[]) =>
  normalize(data, [category]);

export const normalizeImports = (data: ProductsImportHistoryRecord[]) =>
  normalize(data, [productsImport]);

export const normalizeGiftCardImports = (
  data: GiftcardsImportHistoryRecord[]
) => normalize(data, [giftcardsImport]);

const normalizeVarAttrs = (data: { [index: string]: string[] }) =>
  Object.keys(data).map((key) => ({
    key,
    value: data[key][0] as string,
  }));

export const normalizeAddedImage = (img: Image) => ({
  id: img.id,
  uid: img.id.toString(),
  name: `image-${img.id}`,
  url:
    img.urls.original !== undefined ? rewriteConnectUrl(img.urls.original) : '',
  new: img.new,
  size: 0,
  status: 'done' as const,
  type: '' as const,
  urls: Object.keys(img.urls).reduce((acc, key) => {
    const url = img.urls[key as keyof typeof img.urls];
    const modifiedUrl = url.includes('connect')
      ? url.replace('connect', 'app')
      : url;
    return { ...acc, [key]: modifiedUrl };
  }, {} as { [key in keyof typeof img.urls]: string }),
});

const getProdTypeValue = (type: string, variation_count: number) => {
  if (type === 'service') return type;
  return variation_count > 0 ? 'prod_with_vars' : 'single';
};

export const transformProductFields = (p: Product | ProductVariation) => ({
  ...p,
  margin:
    p.price !== null && p.average_purchase_price !== null
      ? parseInt(p.price, 10) - parseInt(p.average_purchase_price, 10)
      : 0,
  attributes: normalizeVarAttrs(p.attributes),
  images: p.images.map(normalizeAddedImage),
});

export const normalizeProduct = (p: Product): NormalizedProduct => ({
  ...p,
  ...transformProductFields(p),
  variations: p.variations
    ? p.variations.map((v) => ({
        ...v,
        uuid: v4(),
        ...transformProductFields(v),
      }))
    : undefined,
  type: p.type,
  giftcard_template: p.giftcard_template,
  ledger_account: p.ledger_account,
});

export const transformGiftcardFields = (p: Product) => ({
  ...p,
  images: p.images.map(normalizeAddedImage),
});

export const normalizeGiftcard = (p: Product): NormalizedGiftCard => ({
  ...p,
  ...transformGiftcardFields(p),
  type: 'giftcard',
  giftcard_template: p.giftcard_template,
});

export const createEmptyVariation = (
  attributes: { key: string; value: string }[]
): LocalVariation => ({
  uuid: v4(),
  attributes,
  gross_price: undefined,
  purchase_price: undefined,
  recommended_price: undefined,
  quantity: undefined,
  reorder_level: undefined,
  safety_stock: undefined,
  product_code: '',
  gtin: '',
  images: [],
  margin: undefined,
});

export const transformAdjustStockToCoreApi = (data: {
  amount: number;
  id: number;
  purchase_price: number;
  reason?: string;
  operation_type: 'ADD' | 'DEDUCT';
}) => ({
  stock: {
    quantity:
      data.operation_type === 'ADD' ? data.amount : R.negate(data.amount),
    price: data.purchase_price,
    note: data.reason || '',
    date: dayjs().format('DD.MM.YYYY'),
  },
});

export const mapProductDataToFormFields = (
  p: Modify<NormalizedProduct, { id?: number }>
) => {
  const fields: FieldData[] = [
    ...R.toPairs(R.omit(['attributes'], p)).map((pair) => ({
      name: pair[0],
      value: pair[1],
    })),
    ...(
      [
        'gross_price',
        'recommended_price',
        'purchase_price',
        'average_purchase_price',
      ] as Array<keyof NormalizedProduct>
    ).map((field) => ({
      name: field,
      value:
        p[field] && p.variation_count === 0
          ? parseFloat(p[field] as string)
          : undefined,
    })),
    ...(
      ['quantity', 'reorder_level', 'safety_stock'] as Array<
        keyof NormalizedProduct
      >
    ).map((field) => ({
      name: field,
      value: R.prop(field, p) ? num(p[field]).format('0') : undefined,
    })),
    {
      name: 'prod_type',
      value: getProdTypeValue(p.type, p.variation_count),
    },
    {
      name: 'custom_price',
      value: p.custom_price === true ? 'flexible' : 'fixed',
    },
    {
      name: 'categories',
      value: p.categories.map((c) => c.id),
    },
  ];

  if ((p.attributes && p.attributes.length > 0) || p.variation_count > 0) {
    fields.push({
      name: 'attributes',
      value:
        p.variation_count === 0
          ? p.attributes
          : getAttributeSetsFromVariations(p.variations ?? []),
    });
  }
  return fields;
};

const simplifyCategory = R.pick(['id', 'name']);

export const findAndSimplifyCategories = (
  categories: Category[],
  ids: number[]
) => categories.filter((c) => ids.includes(c.id)).map(simplifyCategory);

export const mapProductsToTableRecord = (
  products: ProductListRawRecord[],
  categories: Category[]
) => {
  const variations = products.reduce(
    (acc: NormalizedVariationListRecord[], p) => {
      if (p.type === 'gift_card' || p.parent !== null) {
        acc.push({
          ...p,
          meta: {
            isVariation: true,
            hasVariations: false,
            isSingle: false,
            isParent: false,
          },
        });
      }
      return acc;
    },
    []
  );
  const groupedVariations = R.groupBy((v) => v.parent as number, variations);
  return products.reduce((acc: PSGTableDataType[], p) => {
    const extractedCategories = !p.type
      ? R.pluck('id', p.categories)
      : p.categories;
    if (p.parent === null || p.parent === undefined) {
      const vars = groupedVariations[p.id] ?? [];
      const normalizedProduct: PSGTableDataType = {
        ...p,
        variationsIds: R.pluck('id', vars),
        categories: findAndSimplifyCategories(categories, extractedCategories),
        meta: {
          isVariation: false,
          hasVariations: vars.length > 0,
          isParent: vars.length > 0,
          isSingle: vars.length === 0,
        },
      };
      if (vars.length > 0) {
        normalizedProduct.variations = vars;
      }
      acc.push(normalizedProduct);
    }
    return acc;
  }, []);
};

export const mapStocktakingImportEventToJob = (
  ev: StockImportJobEv
): StocktakingImportJob => ({
  id: ev.id,
  type: ev.type,
  type_display: ev.type,
  time_added: ev.time_added,
  time_modified: ev.time_added,
  initiator: null,
  progress: null,
  result: ev.result,
  state: ev.state,
  state_display: ev.state,
  arguments: ev.arguments,
});

export const mapGiftCardDataToForm = (
  gc: Modify<NormalizedGiftCard, { id?: number }>
) => {
  const fields: FieldData[] = [
    ...R.toPairs(R.omit(['attributes'], gc)).map((pair) => ({
      name: pair[0],
      value: pair[1],
    })),
    ...(
      ['gross_price', 'average_purchase_price'] as Array<
        keyof NormalizedProduct
      >
    ).map((field) => ({
      name: field,
      value:
        gc[field] && gc.variation_count === 0
          ? parseFloat(gc[field] as string)
          : undefined,
    })),
    ...(['quantity'] as Array<keyof NormalizedProduct>).map((field) => ({
      name: field,
      value: R.prop(field, gc) ? num(gc[field]).format('0') : undefined,
    })),
    {
      name: 'prod_type',
      value: getProdTypeValue(gc.type, gc.variation_count),
    },
    {
      //In GiftCardDetailForm has the tracking of selling_price to gross_price
      name: 'selling_price',
      value: gc.gross_price,
    },
    {
      name: 'custom_price',
      value: gc.custom_price === true ? 'flexible' : 'fixed',
    },
    {
      name: 'categories',
      value: gc.categories.map((c: Category) => c.id),
    },
  ];
  return fields;
};
